import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import {
  Analytics,
  Currency,
  Lang,
  ErrorDialog,
  Order,
  HotelOffers,
  Utils,
  User,
} from 'types';
import { get as getCookie, set as setCookie } from 'es-cookie';
import { intercom } from '@/plugins';
import i18n from '@/plugins/i18n';
import { UserService } from 'api';

export class State {
  offerId: string = guid();
  useCaseId: string = guid();
  tmpUseCaseId: string = '';
  bookingId: string = guid();
  refreshDialog: ErrorDialog = {
    show: false,
    message: null,
  };
  timeoutEndtime: number = null;
  isAppView: boolean = false;
  currency: Currency.CurrencyItem = getCurrency();
  lang: Lang.LangItem = getLang();
  isMobile: boolean = false;
  networkEnable: boolean = true;
  actualVersion: string = '';
  versions: { relevant: string; current: string } = {
    relevant: '',
    current: '',
  };

  // testmode
  supplierMetaData: any = null;
  isConfiguratorModalOpen: boolean = false;

  exchangeRates: Utils.CurrencyExchange.Rates = null;
  countries: Utils.Countries.Country[] = [];
  welcomeDialog: {
    name: string;
    isShown: boolean;
  } = {
    isShown: false,
    name: '',
  };
  cobrand: Utils.Cobrand.Params = Utils.Cobrand.get();
}

const getters = <GetterTree<State, any>>{
  termsLinkByLocale(): string {
    return '/static/docs/terms.html';
  },

  privacyPolicyLinkByLocale(): string {
    return '/static/docs/privacy.html';
  },

  isWhiteLable(state: State): boolean {
    return state.cobrand === Utils.Cobrand.Params.Anna;
  },
};

const mutations = <MutationTree<State>>{
  setUseCaseId(state: State, useCaseId: string) {
    state.useCaseId = useCaseId;
  },
  setOfferId(state: State, offerId: string) {
    state.offerId = offerId;
  },
  setTmpUseCaseId(state: State, id: string) {
    state.tmpUseCaseId = id;
  },
  setBookingId(state: State, id: string) {
    state.bookingId = id;
  },
  setRefreshDialogShow(state: State, value: ErrorDialog) {
    state.refreshDialog = value;
  },
  setTimeoutEndtime(state: State, time: number) {
    state.timeoutEndtime = time;
  },
  setAppView(state: State, isApp: boolean) {
    state.isAppView = isApp;
  },
  setLang(state: State, lang: Lang.LangItem) {
    if (!lang.isAvailable) {
      return;
    }

    state.lang = lang;
    i18n.global.locale = lang.locale as any;
    storeLocalizationByKey(Lang.LANG_COOKIE_NAME, lang.locale);
  },
  setCurrency(state: State, currency: Currency.CurrencyItem) {
    if (!currency.isAvailable) {
      return;
    }
    state.currency = currency;
    storeLocalizationByKey(Currency.CURRENCY_COOKIE_NAME, currency.currency);
  },
  setIsMobile(state: State, isMobile: boolean) {
    state.isMobile = isMobile;
  },
  setNetworkIsEnable(state: State, enable: boolean) {
    state.networkEnable = enable;
  },
  setSupplierMetaData(state: State, metaData: any) {
    state.supplierMetaData = metaData;
  },
  toggleConfiguratorModal(state: State, isOpen: any) {
    state.isConfiguratorModalOpen = isOpen;
  },
  setExchangeRates(state: State, rates: Utils.CurrencyExchange.Rates) {
    state.exchangeRates = rates;
  },
  setCountries(state: State, countries: Utils.Countries.Country[]) {
    state.countries = countries;
  },
  setVersions(state: State, versions: State['versions']) {
    state.versions = versions;
  },
  toggleWelcomeDialog(
    state: State,
    { isShown, name }: { isShown: boolean; name?: string },
  ) {
    if (isShown && name) {
      state.welcomeDialog = {
        isShown,
        name,
      };
      return;
    }

    state.welcomeDialog = {
      isShown: false,
      name: '',
    };
  },
  setCobrand(state: State, cobrand: Utils.Cobrand.Params) {
    state.cobrand = cobrand;
    // Utils.Cobrand.store(cobrand);
  },
};

let timeout;
const actions = <ActionTree<State, any>>{
  refreshUseCaseId(store: ActionContext<State, any>) {
    store.commit('setUseCaseId', guid());
  },
  refreshOfferId(store: ActionContext<State, any>) {
    store.commit('setOfferId', guid());
  },
  refreshBookingId(store: ActionContext<State, any>) {
    store.commit('setBookingId', guid());
  },
  setTmpUseCaseId(store: ActionContext<State, any>, useCaseId: string) {
    store.commit('setTmpUseCaseId', useCaseId);
  },
  getIds(store: ActionContext<State, any>): Promise<Analytics.Identifiers> {
    return new Promise(resolve => {
      resolve({
        useCaseId: store.state.useCaseId,
        offerId: store.state.offerId,
        bookingId: store.state.bookingId,
      });
    });
  },
  selectSuggest(
    store: ActionContext<State, any>,
    suggest: Analytics.SuggestSelect,
  ) {
    // Events.suggestSelected(suggest.suggest, suggest.position);
  },

  setRefreshDialogShow(store: ActionContext<State, any>, value: ErrorDialog) {
    store.commit('setRefreshDialogShow', value);
  },

  stopCountdown(store: ActionContext<State, any>) {
    clearTimeout(timeout);
    store.commit('setTimeoutEndtime', 0);
  },

  async initResfreshCountdown(
    store: ActionContext<State, any>,
    { time, date }: { time?: number; date?: Date },
  ) {
    let interval;
    if (date) {
      await store.dispatch('stopCountdown');
      await store.commit('setTimeoutEndtime', date.getTime());
      interval = date.getTime() - Date.now();
    } else {
      interval = time ? time : 120 * 60 * 1000; // 120 minutes
      await store.dispatch('stopCountdown');
      await store.commit('setTimeoutEndtime', Date.now() + interval);
    }

    timeout = setTimeout(() => {
      if (store.state.networkEnable) {
        store.commit('setRefreshDialogShow', { show: true, message: null });
        store.dispatch('hotel/checkExpiration', {}, { root: true });
        store.dispatch('search/checkExpiration', {}, { root: true });
      }
    }, interval);
  },

  checkRefreshTimeout(store: ActionContext<State, any>) {
    store.dispatch('hotel/checkExpiration', {}, { root: true });
    store.dispatch('search/checkExpiration', {}, { root: true });

    if (store.state.timeoutEndtime) {
      if (store.state.timeoutEndtime > Date.now()) {
        const remaintingTime = store.state.timeoutEndtime - Date.now();
        store.dispatch('initResfreshCountdown', { time: remaintingTime });
      } else {
        store.dispatch('setRefreshDialogShow', { show: true, message: null });
      }
    }
  },

  async getInfoUtils(store: ActionContext<State, any>): Promise<{
    countries: Utils.Countries.Country[];
    currencies: Utils.CurrencyExchange.Rates;
  }> {
    try {
      const { data } = await UserService.ML.getInfoUtils().toPromise();
      const countries = data.countries.map(
        it => new Utils.Countries.Country(it),
      );
      const currencies = { ...data.currencies, createdAt: new Date() };
      store.commit('setCountries', countries);
      store.commit('setExchangeRates', currencies);

      const storedGuestDetails = User.PersistenceGuestData.get();
      const phone =
        storedGuestDetails?.phone ?? store.rootState.user.userV2?.phone;

      const country = phone
        ? countries.find(it => phone.startsWith(it.code))
        : null;

      if (country) {
        store.commit('user/setPhone', phone, { root: true });
      } else {
        store.commit('user/setPhone', countries[0] ? countries[0].code : '', {
          root: true,
        });
      }

      return {
        countries,
        currencies,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  },

  updateIntercomSettings(
    store: ActionContext<State, any>,
    { order }: { order?: Order.Ml.Response.Order },
  ) {
    const user = store.rootState.user.userV2;
    const destination = store.rootState.searchCriteria.destination;

    let params = {
      user,
      hotelSearchParams: {
        destination,
      },
    };

    const hotel: string = store.rootGetters['hotel/hotelName'];
    const offer: HotelOffers.Offer = store.rootState.booking.offer;

    if (hotel) {
      params.hotelSearchParams['hotel'] = hotel;
    }

    if (order) {
      params['order'] = order;
    }

    if (offer) {
      params.hotelSearchParams['room'] = offer.originalName;

      const pricePerNight = offer.price.effectivePrice;
      const currency = store.state.currency.currency;

      const formatted = pricePerNight.formatCurrency({
        currency: currency,
      });

      params.hotelSearchParams['room_price'] = formatted;
    }

    intercom.update(params);
  },
};

export const support = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export function guid() {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4() + s4() + s4()}`;
}

export default {};

function getLang(): Lang.LangItem {
  const url = window.location;
  const params = new URLSearchParams(url.search);
  const langParam = params.get('lang');
  const lang = langParam
    ? Lang.langsList().find(
        it =>
          it.locale.toLowerCase() === langParam?.toLowerCase() &&
          it.isAvailable,
      )
    : null;

  if (lang) {
    i18n.global.locale = lang.locale as typeof i18n.global.locale;
    storeLocalizationByKey(Lang.LANG_COOKIE_NAME, lang.locale);
    return lang;
  }

  const langCookie = getCookie(Lang.LANG_COOKIE_NAME);
  const langSync = Lang.langsList().find(
    it => it.locale === langCookie && it.isAvailable,
  );
  const locale = langCookie && langSync ? langSync : Lang.DEFAULT_LANG();
  i18n.global.locale = locale.locale as typeof i18n.global.locale;
  return locale;
}

export function getCurrency(): Currency.CurrencyItem {
  const url = window.location;
  const params = new URLSearchParams(url.search);
  const currencyParam = params.get('currency');
  const currency = currencyParam
    ? Currency.currenciesList().find(
        it =>
          it.currency.toLowerCase() === currencyParam?.toLowerCase() &&
          it.isAvailable,
      )
    : null;

  if (currency) {
    storeLocalizationByKey(Currency.CURRENCY_COOKIE_NAME, currency.currency);
    return currency;
  }

  const currencyCookie = getCookie(Currency.CURRENCY_COOKIE_NAME);
  const currencySync = Currency.currenciesList().find(
    it => it.currency === currencyCookie && it.isAvailable,
  );

  return currencyCookie && currencySync
    ? currencySync
    : Currency.DEFAULT_CURRENCY();
}

function storeLocalizationByKey(key: string, arg: string) {
  const prevCookie = getCookie(key);
  const prevStorage = localStorage.getItem(key);
  if (arg === prevCookie && arg === prevStorage) {
    return;
  }
  setCookie(key, arg);
  localStorage.setItem(key, arg);
}
